/**
 * Global auth state — current user and effective permissions.
 *
 * Zustand is used for simplicity; the store is tiny and almost entirely
 * loaded from the /api/auth/me endpoint. Any change to a user's roles on
 * the backend requires a refetch, which the /me hook triggers on window
 * focus.
 */

import { create } from 'zustand';
import { apiRequest } from '@/api/client';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  roles: string[];
  permissions: string[];
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AuthState {
  status: 'unknown' | 'authed' | 'anonymous';
  user: AuthUser | null;
  permissions: Set<string>;
  fetchMe: () => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: AuthUser) => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  status: 'unknown',
  user: null,
  permissions: new Set<string>(),
  setUser(user) {
    set({ status: 'authed', user, permissions: new Set(user.permissions) });
  },
  async fetchMe() {
    try {
      const user = await apiRequest<AuthUser>('/api/auth/me');
      set({ status: 'authed', user, permissions: new Set(user.permissions) });
    } catch {
      set({ status: 'anonymous', user: null, permissions: new Set() });
    }
  },
  async signOut() {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } catch {
      // even if the call fails, clear local state
    }
    set({ status: 'anonymous', user: null, permissions: new Set() });
  },
}));
